// src/redux/slices/userSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { resetStore } from "./resetStore";
import { getServerUrl } from "../../Utils/serverSelector";
import { apiRequest } from "../../Utils/apiClient";

/* ======================= THUNKS ======================= */

// Создание пользователя
export const addUser = createAsyncThunk(
  "users/addUser",
  async (newUser, { rejectWithValue }) => {
    try {
      const res = await apiRequest("POST", "/users", newUser);
      return res?.data || res;
    } catch (error) {
      return rejectWithValue(error?.message || "Ошибка при создании пользователя");
    }
  }
);

// Получение пользователя по uid (PRIVATE)
export const fetchUserByUid = createAsyncThunk(
  "users/fetchUserByUid",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) throw new Error("UID не найден");
      const res = await apiRequest("GET", `/users/${uid}`);
      return res?.data || res;
    } catch (error) {
      return rejectWithValue(error?.message || "Ошибка при получении данных пользователя");
    }
  }
);

// Получение всех пользователей (PRIVATE)
export const fetchAllUsers = createAsyncThunk(
  "users/fetchAllUsers",
  async (_, { rejectWithValue }) => {
    try {
      const res = await apiRequest("GET", "/users");
      const data = res?.data || res;
      return Array.isArray(data) ? data : [];
    } catch (error) {
      return rejectWithValue(error?.message || "Ошибка при получении списка пользователей");
    }
  }
);

// Получение пользователя по телефону (PRIVATE)
export const fetchUserByPhone = createAsyncThunk(
  "users/fetchUserByPhone",
  async (phone, { rejectWithValue }) => {
    try {
      const res = await apiRequest("GET", `/users/phone/${encodeURIComponent(phone)}`);
      return res?.data || res;
    } catch (error) {
      return rejectWithValue(error?.message || "Ошибка при получении пользователя по телефону");
    }
  }
);

// ─── PUBLIC: проверка существования юзера по телефону (без JWT) ───
// Используется на странице бронирования — не требует авторизации
export const checkUserByPhonePublic = createAsyncThunk(
  "users/checkUserByPhonePublic",
  async (phone, { rejectWithValue }) => {
    try {
      const base = getServerUrl().replace(/\/$/, "");
      const { data } = await axios.get(
        `${base}/public/check-user/${encodeURIComponent(phone)}`
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.message ||
        "Ошибка при проверке пользователя"
      );
    }
  }
);

// Обновление данных пользователя (PRIVATE)
export const updateUserProfile = createAsyncThunk(
  "users/updateUserProfile",
  async ({ uid, updates }, { rejectWithValue }) => {
    try {
      const res = await apiRequest("PATCH", `/users/${uid}`, updates);
      const data = res?.data || res;
      return data?.updatedUser || data;
    } catch (error) {
      return rejectWithValue(error?.message || "Ошибка при обновлении профиля");
    }
  }
);

export const checkUserInLocalStorage = () => (dispatch) => {
  try {
    // ✅ Берём uid клиента — отдельный ключ, не перепутаем с мастером
    const clientUid = localStorage.getItem("user_client_uid");
    if (clientUid) {
      dispatch(fetchUserByUid(clientUid));
    }
    // ❌ Убрали localStorage.getItem("uid") — там может быть uid мастера
  } catch { }
};

/* ======================= STATE ======================= */

const initialState = {
  users: [],
  currentUser: null,
  status: "idle",
  error: null,
};

/* ======================= SLICE ======================= */

const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    logoutUser: (state) => {
      state.currentUser = null;
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addUser.pending, (state) => { state.status = "loading"; state.error = null; })
      .addCase(addUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        if (action.payload) state.users.push(action.payload);
      })
      .addCase(addUser.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(fetchUserByUid.pending, (state) => { state.status = "loading"; state.error = null; })
      .addCase(fetchUserByUid.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.currentUser = action.payload || null;
      })
      .addCase(fetchUserByUid.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
        state.currentUser = null;
      })

      .addCase(fetchAllUsers.pending, (state) => { state.status = "loading"; state.error = null; })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.users = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(fetchUserByPhone.pending, (state) => { state.status = "loading"; state.error = null; })
      .addCase(fetchUserByPhone.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.currentUser = action.payload || null;
      })
      .addCase(fetchUserByPhone.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(checkUserByPhonePublic.pending, (state) => { state.error = null; })
      .addCase(checkUserByPhonePublic.fulfilled, () => {
        // намеренно не меняем state — только возвращаем данные через unwrap()
      })
      .addCase(checkUserByPhonePublic.rejected, (state, action) => {
        state.error = action.payload || action.error?.message;
      })

      .addCase(updateUserProfile.pending, (state) => { state.status = "loading"; state.error = null; })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.currentUser = action.payload || state.currentUser;
        const updated = action.payload;
        if (updated?.uid) {
          const idx = state.users.findIndex((u) => u.uid === updated.uid);
          if (idx !== -1) state.users[idx] = { ...state.users[idx], ...updated };
        }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || action.error?.message;
      })

      .addCase(resetStore, () => initialState);
  },
});

export const { logoutUser } = userSlice.actions;
export default userSlice.reducer;

/* ======================= SELECTORS ======================= */

export const selectUsersState = (s) => s.users;
export const selectAllUsers = (s) => s.users?.users || [];
export const selectCurrentUser = (s) => s.users?.currentUser || null;
export const selectUsersStatus = (s) => s.users?.status || "idle";
export const selectUsersError = (s) => s.users?.error || null;